/**
 * Read a money amount out of whatever a shop theme prints on screen.
 *
 * Its own module because it is the one piece of the e-commerce detector that
 * is pure, and because it is the piece where being wrong costs the most: this
 * number becomes the conversion value we report to every ad platform. A
 * purchase that arrives at a tenth of its worth teaches the platform to bid
 * like the customer is worth a tenth.
 *
 * The old version did `replace(",", ".")` on the first comma it found. That is
 * correct for "99,90" and wrong for every thousands separator: "1.234,56"
 * became "1.234.56" and parsed as 1.23, and "1,234.56" did the same. Nothing
 * reported it, because a number that parses is a number that looks fine.
 * Amounts under a thousand hid it; the moment an order crossed that line the
 * value silently collapsed.
 */

/**
 * Which of the two separators marks the decimals, if any.
 *
 * With both present the LAST one wins: it is the only one that can be the
 * decimal separator, whichever locale wrote it. With one present it comes down
 * to how many digits trail it, and there the rule is deliberately blunt:
 * exactly three trailing digits reads as a thousands separator ("1.234" is
 * twelve hundred and thirty-four, in both conventions), anything else reads as
 * decimals. A price with three decimals exists, but not on a checkout page.
 */
const DECIMAL_DIGITS_MAX = 2;
const GROUP_SIZE = 3;

const decimalSeparator = (digitsAndSeparators: string): "." | "," | null => {
  const lastDot = digitsAndSeparators.lastIndexOf(".");
  const lastComma = digitsAndSeparators.lastIndexOf(",");
  if (lastDot === -1 && lastComma === -1) return null;

  if (lastDot !== -1 && lastComma !== -1) {
    return lastDot > lastComma ? "." : ",";
  }

  const only = lastDot !== -1 ? "." : ",";
  const position = lastDot !== -1 ? lastDot : lastComma;
  const trailing = digitsAndSeparators.length - position - 1;

  // Two of the same separator can only be grouping: "1.234.567".
  const occurrences = digitsAndSeparators.split(only).length - 1;
  if (occurrences > 1) return null;

  if (trailing === GROUP_SIZE) return null;
  if (trailing > 0 && trailing <= DECIMAL_DIGITS_MAX) return only;
  return null;
};

export function parsePrice(text: string | null | undefined): number | undefined {
  if (!text) return undefined;

  // Pick the amount as a token instead of stripping everything that is not a
  // digit. Stripping glues unrelated digits together: "2 items - €99,90"
  // becomes "299,90", and the stray dot in "incl. btw" turns a clean amount
  // into nonsense. The longest run of digits and separators is the amount;
  // any count or label around it is shorter.
  const tokens = text.match(/\d+(?:[.,]\d+)*/g);
  if (!tokens || tokens.length === 0) return undefined;
  const cleaned = tokens.reduce((longest, token) => (token.length > longest.length ? token : longest));

  const separator = decimalSeparator(cleaned);
  const withoutGrouping = separator === null
    ? cleaned.replace(/[.,]/g, "")
    : cleaned.split(separator === "." ? "," : ".").join("");

  const normalized = separator === "," ? withoutGrouping.replace(",", ".") : withoutGrouping;
  const value = parseFloat(normalized);
  return isNaN(value) ? undefined : value;
}

/**
 * An amount that an API reported in minor units.
 *
 * WooCommerce's Store API answers with integers and a separate
 * `currency_minor_unit`: 4995 with unit 2 is 49.95. Reading the integer as a
 * plain number reports a cart a hundred times too large, and an ad platform
 * will happily optimise towards that number without ever complaining.
 *
 * Returns undefined rather than a wrong number when either input is unusable,
 * because a checkout start with no value is poor and one with a value of 4995
 * euros is actively harmful.
 */
export function minorUnitsToAmount(raw: unknown, minorUnit: unknown): number | undefined {
  // Checked before Number(), because Number(null) and Number("") are both 0
  // and isFinite is perfectly happy with that. A missing total would then be
  // reported as a conversion worth zero, which is the same class of lie this
  // function exists to prevent, only quieter.
  if (raw === null || raw === undefined || raw === "") return undefined;

  const amount = Number(raw);
  if (!isFinite(amount)) return undefined;

  const unit = Number(minorUnit);
  // A missing unit means two, which is what every currency this runs against
  // uses. A negative or absurd one means the response is not what we think it
  // is, and guessing at that point is how you invent revenue.
  const digits = isFinite(unit) && unit >= 0 && unit <= 4 ? unit : 2;

  return amount / Math.pow(10, digits);
}
